import React from 'react';
import {
  Container, Typography, Grid, Card, CardContent, CardMedia, Button,
  Pagination, Box, Chip, CircularProgress, Alert, Tabs, Tab, Paper,
  FormControl, InputLabel, Select, MenuItem, TextField, Snackbar,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Popover,
  Checkbox, FormControlLabel,
} from '@mui/material';
import {
  KeyboardArrowDown, ViewList, ExpandMore, OpenInNew, ArrowUpward,
  ArrowDownward, ChevronLeft, ChevronRight,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format } from 'date-fns';
import BlogDialog from '../../../components/BlogDialog';
import { useBlogContext } from '../../BlogContext';
import { mapIdToShortString, getDaysInMonth, getFirstDayOfMonth, formatDateDisplay, formatDateRange, formatDateForInput, isSameDay, isDateInRange } from '../blogUtils';

const BlogPostsTab = () => {
  const {
    isAdmin, posts, page, totalPages, loading, error, selectedTag,
    setSelectedTag, dialogOpen, setDialogOpen, dialogMode, selectedPost,
    tabValue, setTabValue, categories, preBuiltCategory, preBuiltSubcategory,
    rankingCriteria, setRankingCriteria, preBuiltLoading, mergedBlogs,
    loadingMergedBlogs, blogPosts, blogPerformanceData, mergedBlogPerformanceData,
    blogPageStats, sourceBlogMap, pausedBlogs, expandedStatsDialog,
    setExpandedStatsDialog, mergedBlogsLimit, setMergedBlogsLimit,
    blogsPerMergedBlogLimit, setBlogsPerMergedBlogLimit,
    mergedBlogsStartDate, mergedBlogsEndDate,
    dateRangePickerOpen, setDateRangePickerOpen,
    dateRangePickerAnchor, setDateRangePickerAnchor,
    tempStartDate, setTempStartDate, tempEndDate, setTempEndDate,
    startMonth, setStartMonth, endMonth, setEndMonth,
    includeToday, setIncludeToday, snackbar, setSnackbar,
    handleCloseSnackbar, handlePageChange, handleTagClick,
    handleCreatePost, handleViewPost, handleEditPost,
    handleCloseDialog, handleDialogSuccess,
    handlePreBuiltCategoryChange, handlePreBuiltSubcategoryChange,
    fetchMergedBlogs, handleReorderBlogsInMerged, handleRemoveBlogFromMerged,
    isAuthenticated, user,
    showSnackbar, reloadMergedBlog,
    setPausedBlogs,
    setMergedBlogsStartDate, setMergedBlogsEndDate,
    allTags, blogActions,
  } = useBlogContext();

  return (
    <>
      {/* Tags filter */}
      {allTags.length > 0 && (
        <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {allTags.map(tag => (
            <Chip
              key={tag}
              label={tag}
              onClick={() => handleTagClick(tag)}
              color={selectedTag === tag ? 'primary' : 'default'}
              clickable
            />
          ))}
          {selectedTag && (
            <Button
              size="small"
              onClick={() => setSelectedTag(null)}
              sx={{ ml: 1 }}
            >
              Clear filter
            </Button>
          )}
        </Box>
      )}
    
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}
    
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : posts.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6">
            {selectedTag
              ? `No posts found with tag "${selectedTag}"`
              : 'No blog posts yet'}
          </Typography>
          {isAuthenticated && (
            <Button
              onClick={handleCreatePost}
              variant="contained"
              sx={{ mt: 2 }}
            >
              Create the first post
            </Button>
          )}
        </Box>
      ) : (
        <>
          <Grid container spacing={4}>
            {posts.map(post => (
              <Grid item xs={12} sm={6} md={4} key={post._id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 6
                    }
                  }}
                >
                  {post.featuredImage && (
                    <CardMedia
                      component="img"
                      height="140"
                      image={post.featuredImage}
                      alt={post.title}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography gutterBottom variant="h5" component="h2" noWrap>
                      {post.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        mb: 2
                      }}
                    >
                      {post.content.replace(/<[^>]*>/g, '').slice(0, 150)}...
                    </Typography>
    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                      <Typography variant="caption" color="text.secondary">
                        {post.createdAt && format(new Date(post.createdAt), 'MMM d, yyyy')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          onClick={() => handleViewPost(post)}
                          size="small"
                          startIcon={<VisibilityIcon />}
                        >
                          View
                        </Button>
                        {isAuthenticated && post.author?._id === user?.id && (
                          <Button
                            onClick={() => handleEditPost(post)}
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                          >
                            Edit
                          </Button>
                        )}
                      </Box>
                    </Box>
    
                    {post.tags && post.tags.length > 0 && (
                      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {post.tags.map(tag => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              handleTagClick(tag);
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
    
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    
      {/* Blog Dialog - Advanced Popup */}
      <BlogDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        mode={dialogMode}
        blogPost={selectedPost}
        onSuccess={handleDialogSuccess}
      />
    </>
  );
};

export default BlogPostsTab;
